import { PaginationState, SortingState } from '@tanstack/react-table';
import { z } from 'zod';

export const ImportTypeInsuranceCarrierSchema = z.object({
	importTypeNAICXrefId: z.number().nullable().optional(),
	importTypeLobId: z.number(),
	naicId: z.number(),
	naicCode: z.string().nullable().optional(),
	companyName: z.string().nullable().optional(),
});

export type ImportTypeInsuranceCarrier = z.infer<
	typeof ImportTypeInsuranceCarrierSchema
>;

export type SelectOption = {
	value: number;
	label: string;
};

export const all = async (
	pagination: PaginationState,
	sorting: SortingState = [],
	importTypeId: number,
	params: object,
): Promise<{ data: ImportTypeInsuranceCarrier[]; total: number }> => {
	let queryString = '';
	if (params) {
		const paramsAsString = JSON.stringify(params)
			.replace(/:/g, '=')
			.replace(/,/g, '&')
			.replace(/[{}"]/g, '');

		const searchParams = new URLSearchParams(paramsAsString);
		if (pagination) {
			searchParams.append('pageSize', pagination.pageSize.toString());
			searchParams.append('pageIndex', pagination.pageIndex.toString());
		}

		if (sorting.length > 0) {
			searchParams.append('sortBy', sorting[0].id);
			searchParams.append('sortDirection', sorting[0].desc ? 'desc' : 'asc');
		}

		queryString = `?${searchParams.toString()}`;
	}

	const response = await fetch(
		`/admin/insurancecarrier/${importTypeId}${queryString}`,
	);

	return await response.json();
};

export const add = async (
	insuranceCarrier: ImportTypeInsuranceCarrier,
): Promise<boolean> => {
	const res = await fetch(`/admin/insurancecarrier`, {
		method: 'POST',
		body: JSON.stringify(insuranceCarrier),
		headers: {
			'Content-Type': 'application/json',
		},
	});
	return res.ok;
};

export const update = async (
	insuranceCarrier: ImportTypeInsuranceCarrier,
): Promise<boolean> => {
	const res = await fetch(`/admin/insurancecarrier`, {
		method: 'PATCH',
		body: JSON.stringify(insuranceCarrier),
		headers: {
			'Content-Type': 'application/json',
		},
	});
	return res.ok;
};

export const getLobOptions = async (
	importTypeId: number,
): Promise<SelectOption[]> => {
	const response = await fetch(
		`/admin/insurancecarrier/lob/options/${importTypeId}`,
	);
	return await response.json();
};

export const getNaicOptions = async (
	importTypeLobId: number,
	selected?: number,
): Promise<SelectOption[]> => {
	const params = selected ? `?selected=${selected}` : '';
	const response = await fetch(
		`/admin/insurancecarrier/naic/options/${importTypeLobId}${params}`,
	);
	return await response.json();
};
